




















































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import { ICurrency } from "@/models/constants";
import { IInvoice } from "@/models/invoices";
import { InvoiceStatusMap } from "@/data/enums/invoice";

import Context from "@/mixins/context";
import _ from "@/boot/lodash";
import i18n from "@/i18n";
import { BrandConfigKeys } from "@/data/constants";

@Component
export default class InvoiceStatusMsg extends Mixins(Context) {
  // Props

  @Prop({ required: true })
  public readonly invoice!: IInvoice;

  @Prop({ default: false })
  public readonly showActions!: boolean;

  @Prop({ default: () => i18n.t("_.invoice") })
  public readonly object!: string;

  public currencyAmounts = {} as Record<string, string | undefined>;

  // Computed

  public get mappedStatus() {
    return InvoiceStatusMap(this.invoice, this.object);
  }

  public get canCredit() {
    if (!this.isAdmin) return false;
    return this.mappedStatus?.actions.includes("credit");
  }

  public get canPay() {
    const isDelegated = this.$store.getters[
      `data/${DataModules.INVOICES}/isDelegated`
    ](this.invoice);
    if (isDelegated) return false;
    return this.mappedStatus?.actions.includes("pay");
  }

  public get hasPartialPayment() {
    return !!this.invoice?.unpaid_amount && !!this.invoice?.paid_amount;
  }

  public get canChangeCurrency(): boolean {
    if (this.hasPartialPayment) return false;
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_DIFFERENT_CURRENCY_PAYMENT_ENABLED
      ] ?? false
    );
  }

  public get unpaidAmountFormatted() {
    return _.compact([
      this.invoice.unpaid_amount_formatted,
      // Here we show alternative preferred currency amount (if we have the data)
      this.preferredPaymentCurrencyCode !== this.invoice?.currency?.code &&
      this.preferredPaymentCurrencyAmount
        ? `(${this.preferredPaymentCurrencyAmount} ${this.preferredPaymentCurrencyCode})`
        : null
    ]).join(" ");
  }

  public get dropdownCurrencies() {
    return _.uniq(
      _.compact([
        this.canChangeCurrency &&
          this.invoice?.account?.preferred_payment_currency?.code,
        this.invoice?.currency?.code
      ])
    );
  }

  public get preferredPaymentCurrencyCode() {
    return this.invoice?.account?.preferred_payment_currency?.code;
  }

  public get preferredPaymentCurrencyAmount() {
    return this.currencyAmounts[this.preferredPaymentCurrencyCode as string];
  }

  // Methods

  /**
   * @name getCurrencyAmounts
   * @desc Here we iterate through all preferred currencies (the invoice
   * default currency and the client's preferred currency) to either relay or
   * get the converted unpaid invoice amounts.
   */

  public async getCurrencyAmounts() {
    if (!this.dropdownCurrencies.length) return;
    const promises = [] as Promise<void>[];
    this.dropdownCurrencies.forEach(code => {
      promises.push(
        new Promise(resolve => {
          const { currency, unpaid_amount_formatted } = this.invoice;
          // Get converted currency amount
          if (currency?.code !== code)
            return this.getUnpaidConvertedAmount(code);
          // Get invoice currency amount
          this.$set(
            this.currencyAmounts,
            code,
            this.$store.getters["utils/money/trimTrailingZeros"](
              unpaid_amount_formatted
            )
          );
          // Resolve promise
          return resolve();
        })
      );
    });
    return Promise.all(promises);
  }

  /**
   * @name getUnpaidConvertedAmount
   * @desc Here we fetch the converted unpaid amount for a given currency code.
   * This is retrieved from the API and stored locally within a Record.
   */

  public async getUnpaidConvertedAmount(currency_code: ICurrency["code"]) {
    try {
      const { unpaid_amount_formatted } = await this.$store.dispatch(
        `data/${DataModules.INVOICES}/getUnpaidConvertedAmount`,
        {
          invoiceId: this.invoice.id,
          params: { currency_code },
          vm: this,
          storeData: false,
          returnData: true
        }
      );
      this.$set(
        this.currencyAmounts,
        currency_code,
        this.$store.getters["utils/money/trimTrailingZeros"](
          unpaid_amount_formatted
        )
      );
    } catch {
      // Here we fail silently as outcome is non-critical
    }
  }

  // Watchers

  @Watch("dropdownCurrencies", { immediate: true })
  public onPreferredCurrenciesChange() {
    if (!this.dropdownCurrencies.length) return;
    if (!this.canPay) return;
    this.getCurrencyAmounts();
  }
}
